import type { HoleScoreFragment } from '../../../__generated__/hooks'
import { IconChevronLeft } from '../../../components/Icon'

/**
 * Compact single-band hole hero per UX consult round 1.
 *
 * The 76px hole number + tall illustration was eating ~28% of viewport
 * on a phone — repeated 72 times per round (4 players × 18 holes) by
 * the captain entering scores. Compressed to ~64px total: a row with
 * prev/next + the inline triplet (Hull N · Par P · Idx I).
 *
 * Sticks under the topbar via `top: var(--player-topbar-h)` (published
 * by TopBar's ResizeObserver) so the captain can always jump holes
 * without scrolling back up.
 */
const HoleHeader = ({
  hole,
  total,
  onPrev,
  onNext,
}: {
  hole: HoleScoreFragment['hole']
  total: number
  onPrev: () => void
  onNext: () => void
}) => (
  <header
    className="sticky z-30 overflow-hidden text-paper"
    style={{
      top: 'var(--player-topbar-h, 0px)',
      background: 'linear-gradient(180deg, #0f3a26 0%, #0b2a1c 100%)',
    }}
  >
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          'radial-gradient(circle at 90% -10%, rgba(78, 169, 122, 0.22), transparent 55%)',
      }}
    />
    <div className="relative flex items-center gap-2 px-3 py-2">
      <button
        type="button"
        onClick={onPrev}
        disabled={hole.number <= 1}
        aria-label="Forrige hull"
        className="flex items-center gap-1 px-2 py-1.5 rounded bg-paper/10 hover:bg-paper/15 text-[11px] font-semibold text-green-100/90 disabled:opacity-30 disabled:bg-transparent transition-colors"
      >
        <IconChevronLeft size={14} />
        {hole.number > 1 ? hole.number - 1 : '—'}
      </button>
      <div className="flex-1 flex items-baseline justify-center gap-2">
        <span
          className="font-display font-extrabold text-paper leading-none"
          style={{ fontSize: 28, letterSpacing: '-0.04em' }}
        >
          Hull {hole.number}
        </span>
        <span className="text-[11px] font-semibold text-green-100/80 num">
          ·
        </span>
        <span className="font-display font-bold text-paper text-base">
          Par {hole.par}
        </span>
        <span className="text-[11px] font-semibold text-green-100/80 num">
          ·
        </span>
        <span className="text-[11px] font-semibold text-green-100/80 num">
          Idx <span className="text-paper">{hole.index}</span>
        </span>
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={hole.number >= total}
        aria-label="Neste hull"
        className="flex items-center gap-1 px-2 py-1.5 rounded bg-paper/10 hover:bg-paper/15 text-[11px] font-semibold text-green-100/90 disabled:opacity-30 disabled:bg-transparent transition-colors"
      >
        {hole.number < total ? hole.number + 1 : '—'}
        <IconChevronLeft size={14} className="rotate-180" />
      </button>
    </div>
  </header>
)

export default HoleHeader
